import { format } from 'date-fns'
import {
  IOpsBucketHours,
  IOpsDayReportRow,
  IOpsLossPareto,
  IOpsLossTrendPoint,
  IOpsMachineDay,
  IOpsQueueStarvation,
  IOpsRequestPairStat,
  IOpsSafetyTrendPoint,
  IOpsSegmentContext,
  IOpsShiftReviewEntry,
  IOpsShiftReviewPack,
  IOpsTrendSeries,
  IOpsTumSegment,
  IOpsWorkerDay,
  OpsLossBucket,
  OpsTumActivity,
} from 'types/opsAnalytics.interface'
import { classifyLossBucket } from './lossClassifier'
import { OPS_LIVE_TASKS, OPS_MACHINES_DOWN, OPS_WORKERS } from './opsControlMocks'

const CTX: IOpsSegmentContext = {
  hasTask: true,
  taskIsRepair: false,
  maintIsPlanned: false,
  outOfFuel: false,
  operatorPresent: true,
  blockedByRequest: false,
  zoneOnHold: false,
}

const ctxOf = (over: Partial<IOpsSegmentContext>): IOpsSegmentContext => ({ ...CTX, ...over })

const NO_TASK_CTX = ctxOf({ hasTask: false })
const NO_FUEL_CTX = ctxOf({ outOfFuel: true })
const NO_OPERATOR_CTX = ctxOf({ operatorPresent: false })
const BREAKDOWN_CTX = ctxOf({ taskIsRepair: true })
const PLANNED_CTX = ctxOf({ taskIsRepair: true, maintIsPlanned: true })
const BLOCKED_CTX = ctxOf({ blockedByRequest: true })
const HOLD_CTX = ctxOf({ zoneOnHold: true })

const seg = (
  id: number,
  fromMin: number,
  toMin: number,
  ctx: IOpsSegmentContext,
  activity: OpsTumActivity | null,
  taskName: string | null,
  taskId: number | null,
  location: string | null,
  note: string | null,
): IOpsTumSegment => ({
  id,
  fromMin,
  toMin,
  bucket: classifyLossBucket(ctx),
  activity,
  taskName,
  taskId,
  location,
  note,
})

const CYCLE_PHASES: { activity: OpsTumActivity; durationMin: number; name: string; location: string }[] = [
  { activity: 'load', durationMin: 4, name: 'Haul cycle — load at pit', location: 'Pit A' },
  { activity: 'haul', durationMin: 5, name: 'Haul cycle — travel loaded', location: 'Main Haul Road' },
  { activity: 'dump', durationMin: 2, name: 'Haul cycle — dump at crusher', location: 'Crusher Area' },
  { activity: 'return', durationMin: 4, name: 'Haul cycle — return empty', location: 'Main Haul Road' },
]

const cycleSegs = (idFrom: number, startMin: number, cycles: number): IOpsTumSegment[] => {
  const out: IOpsTumSegment[] = []
  for (let c = 0; c < cycles; c++) {
    let cursor = startMin + c * 15
    CYCLE_PHASES.forEach((p, i) => {
      out.push(seg(idFrom + c * 4 + i, cursor, cursor + p.durationMin, CTX, p.activity, p.name, null, p.location, null))
      cursor += p.durationMin
    })
  }
  return out
}

const BUCKET_ORDER: OpsLossBucket[] = [
  'PRODUCTIVE',
  'BREAKDOWN',
  'PLANNED_MAINT',
  'NO_FUEL',
  'NO_OPERATOR',
  'NO_TASK',
  'BLOCKED_CROSS_DEPT',
  'WEATHER_BLAST_HOLD',
]

const bucketHours = (segments: IOpsTumSegment[]): IOpsBucketHours[] =>
  BUCKET_ORDER.map((bucket) => ({
    bucket,
    hours:
      Math.round(
        segments.filter((s) => s.bucket === bucket).reduce((total, s) => total + s.toMin - s.fromMin, 0) / 6,
      ) / 10,
  })).filter((b) => b.hours > 0)

const mDay = (
  machineId: number,
  machineName: string,
  dateLabel: string,
  operator: string | null,
  segments: IOpsTumSegment[],
  utilizationPct: number,
  availabilityPct: number,
  loads: number,
  tonnes: number,
  fuelAddedL: number,
  smuDeltaH: number,
  breakdownCount: number,
  costOfDay: number,
  shiftSplit?: string,
): IOpsMachineDay => ({
  machineId,
  machineName,
  dateLabel,
  shiftName: 'Day Shift',
  segments,
  hoursByBucket: bucketHours(segments),
  utilizationPct,
  availabilityPct,
  loads,
  tonnes,
  fuelAddedL,
  smuDeltaH,
  breakdownCount,
  costOfDay,
  operator,
  shiftSplit,
})

const truck03Yesterday = (): IOpsTumSegment[] => {
  const night = cycleSegs(200, 735, 22)
  night[0] = { ...night[0], note: 'Night crew — S. Chirwa hauling 18:15–05:30' }
  return [
    ...cycleSegs(1, 10, 21),
    seg(85, 325, 355, PLANNED_CTX, 'task', 'Greasing — 250h lube service', null, 'Fuel bay', 'Scheduled greasing with the lube truck'),
    ...cycleSegs(86, 355, 8),
    seg(118, 475, 520, NO_TASK_CTX, null, null, null, 'Crusher Area', 'Crusher crew change — no follow-on cycle queued'),
    ...cycleSegs(119, 520, 12),
    ...night,
    seg(300, 1065, 1110, PLANNED_CTX, 'task', 'Night lube + inspection — scheduled', null, 'Fuel bay', 'S. Chirwa — planned 45 min service window'),
    seg(301, 1110, 1130, NO_TASK_CTX, null, null, null, 'Crusher Area', 'Crusher feed pause — night crib rotation'),
    ...cycleSegs(310, 1130, 16),
    seg(380, 1370, 1410, CTX, 'task', 'Fuel + washdown — end of night run', null, 'Go-line', 'S. Chirwa parked up 05:30'),
  ]
}

const truck12Yesterday = (): IOpsTumSegment[] => {
  const cycles = cycleSegs(1, 20, 28)
  cycles[0] = { ...cycles[0], note: 'Rotation: 8 ore loads to crusher · 20 waste to South dump' }
  const night = cycleSegs(200, 760, 24)
  night[0] = { ...night[0], note: 'Back in service 18:40 after the tyre retorque — night rotation' }
  return [
    ...cycles,
    seg(113, 440, 626, BREAKDOWN_CTX, 'task', 'Tyre change — Truck 12', 9104, 'Main Haul Road', 'Tyre failure — fitter with tyre handler on it · 3.1h'),
    seg(114, 626, 678, BREAKDOWN_CTX, 'task', 'Torque recheck — replacement tyre', null, 'Workshop Bay 1', 'Workshop hold before release'),
    seg(115, 678, 700, CTX, 'task', 'Roadworthy run — return to pit', null, 'Main Haul Road', null),
    seg(116, 700, 760, BREAKDOWN_CTX, 'task', 'Release inspection — tyre change close-out', null, 'Workshop Bay 1', 'Released to night shift 18:40'),
    ...night,
    seg(300, 1120, 1150, NO_TASK_CTX, null, null, null, 'Crib room', 'Night crib rotation — queue idle'),
    ...cycleSegs(310, 1150, 16),
    seg(380, 1390, 1410, CTX, 'task', 'Fuel + washdown — end of night run', null, 'Go-line', null),
  ]
}

const cat320Yesterday = (): IOpsTumSegment[] => [
  seg(1, 10, 240, CTX, 'task', 'Excavate bench segment 4A', null, 'Pit B', null),
  seg(2, 240, 480, CTX, 'task', 'Excavate bench segment 4B', null, 'Pit B', null),
  seg(3, 480, 530, HOLD_CTX, null, null, null, 'Pit B', 'Pit B hold — blast window'),
  seg(4, 530, 700, CTX, 'task', 'Excavate bench segment 4B', null, 'Pit B', null),
  seg(5, 720, 770, HOLD_CTX, null, null, null, 'Pit B', 'Night blast clearance — waiting on the all-clear'),
  seg(6, 770, 1100, CTX, 'task', 'Excavate bench segment 5A', null, 'Pit B', null),
  seg(7, 1100, 1410, CTX, 'task', 'Excavate bench segment 5B — night advance', null, 'Pit B', null),
]

const lateStartSegs = (taskName: string, location: string): IOpsTumSegment[] => [
  seg(1, 0, 25, NO_OPERATOR_CTX, null, null, null, 'Go-line', 'Operator 25 min late — covered from reserve roster'),
  seg(2, 25, 360, CTX, 'haul', taskName, null, location, null),
  seg(3, 360, 395, NO_TASK_CTX, null, null, null, 'Crib room', 'Crib rotation'),
  seg(4, 395, 700, CTX, 'haul', taskName, null, location, null),
]

const truck05Yesterday = (): IOpsTumSegment[] => [
  seg(1, 0, 25, NO_OPERATOR_CTX, null, null, null, 'Go-line', 'Operator 25 min late — covered from reserve roster'),
  seg(2, 25, 360, CTX, 'haul', 'Waste haulage', null, 'Pit B → South dump', null),
  seg(3, 360, 395, NO_TASK_CTX, null, null, null, 'Crib room', 'Crib rotation'),
  seg(4, 395, 420, NO_FUEL_CTX, null, null, null, 'Pit A', 'Waited at Pit A bowser — tank queue'),
  seg(5, 420, 700, CTX, 'haul', 'Waste haulage', null, 'Pit B → South dump', null),
]

const dozerYesterday = (): IOpsTumSegment[] => [
  seg(1, 10, 420, CTX, 'task', 'Dump face maintenance', null, 'South dump', null),
  seg(2, 420, 480, BLOCKED_CTX, null, 'Hydraulic hose — parts request', null, 'Workshop Bay 2', 'Waiting on warehouse parts request — hose 25mm'),
  seg(3, 480, 715, CTX, 'task', 'Ramp regrade — Pit B access', null, 'Pit B', null),
  seg(4, 715, 720, BREAKDOWN_CTX, 'task', 'Hydraulic hose failure', null, 'South dump', 'Hose let go on final pass — parked for overnight repair'),
]

const stdSegs = (activity: OpsTumActivity, taskName: string, location: string): IOpsTumSegment[] => [
  seg(1, 10, 350, CTX, activity, taskName, null, location, null),
  seg(2, 350, 380, NO_TASK_CTX, null, null, null, 'Crib room', 'Crib rotation — queue idle'),
  seg(3, 380, 700, CTX, activity, taskName, null, location, null),
]

const cat320ServiceDay = (): IOpsTumSegment[] => [
  seg(1, 0, 720, PLANNED_CTX, 'task', 'Track service — 2,000h planned rebuild', null, 'Workshop Bay 1', null),
]

const truck12DownDay = (): IOpsTumSegment[] => [
  seg(1, 0, 430, BREAKDOWN_CTX, 'task', 'Tyre failure — sidewall and rim', null, 'Workshop Bay 1', 'Second tyre failure in a week'),
  seg(2, 430, 720, BLOCKED_CTX, null, 'Awaiting tyre stock', null, 'Workshop Bay 1', 'Tyre 18.00R33 out of stock — warehouse request open'),
]

const dozerOverheatDay = (): IOpsTumSegment[] => [
  seg(1, 0, 300, CTX, 'task', 'Dump face maintenance', null, 'South dump', null),
  seg(2, 300, 600, BREAKDOWN_CTX, 'task', 'Final drive overheat', null, 'South dump', 'Cooled, inspected and released'),
  seg(3, 600, 720, CTX, 'task', 'Dump face maintenance', null, 'South dump', null),
]

const PRIOR_LABELS = ['30 Jun', '29 Jun', '28 Jun', '27 Jun', '26 Jun']

const T03_PRIOR = [
  [79, 100, 44, 1655, 395, 11.8, 0, 2210],
  [84, 100, 44, 1650, 390, 11.6, 0, 2180],
  [80, 100, 41, 1540, 360, 11.2, 0, 2120],
  [83, 100, 43, 1610, 385, 11.5, 0, 2150],
  [81, 100, 42, 1575, 370, 11.4, 0, 2100],
]

const T05_PRIOR = [
  [76, 100, 36, 1080, 330, 10.6, 0, 2010],
  [81, 100, 40, 1200, 360, 11.3, 0, 2080],
  [77, 100, 37, 1110, 340, 10.9, 0, 2030],
  [80, 100, 39, 1170, 355, 11.1, 0, 2060],
  [78, 100, 38, 1140, 350, 11.0, 0, 2040],
]

const T08_PRIOR = [
  [80, 100, 42, 1260, 380, 11.5, 0, 2130],
  [82, 100, 41, 1230, 370, 11.4, 0, 2090],
  [78, 100, 38, 1140, 345, 11.0, 0, 2040],
  [81, 100, 40, 1200, 365, 11.2, 0, 2070],
  [79, 100, 39, 1170, 355, 11.1, 0, 2050],
]

const T12_PRIOR = [
  [0, 0, 0, 0, 0, 0.2, 1, 1450],
  [80, 100, 39, 1460, 355, 11.2, 0, 2070],
  [76, 100, 35, 1310, 335, 10.7, 0, 2010],
  [79, 100, 38, 1425, 350, 11.0, 0, 2050],
  [77, 100, 36, 1350, 340, 10.8, 0, 2020],
]

const CAT_PRIOR = [
  [89, 100, 0, 0, 310, 11.2, 0, 1985],
  [91, 100, 0, 0, 320, 11.4, 0, 2000],
  [88, 100, 0, 0, 305, 11.0, 0, 1970],
  [0, 0, 0, 0, 40, 0.8, 0, 1650],
  [90, 100, 0, 0, 315, 11.3, 0, 1990],
]

const D1_PRIOR = [
  [55, 58, 0, 0, 210, 7.9, 1, 1680],
  [77, 100, 0, 0, 290, 11.1, 0, 1750],
  [73, 100, 0, 0, 275, 10.8, 0, 1710],
  [76, 100, 0, 0, 285, 11.0, 0, 1740],
  [74, 100, 0, 0, 280, 10.9, 0, 1720],
]

const priorDay = (
  machineId: number,
  machineName: string,
  label: string,
  operator: string | null,
  segments: IOpsTumSegment[],
  t: number[],
): IOpsMachineDay => mDay(machineId, machineName, label, operator, segments, t[0], t[1], t[2], t[3], t[4], t[5], t[6], t[7])

const priorMachineDays = (): IOpsMachineDay[] => {
  const rows: IOpsMachineDay[] = []
  PRIOR_LABELS.forEach((label, i) => {
    rows.push(priorDay(1, 'Truck 03', label, 'Peter M.', stdSegs('haul', 'Haul cycles — North Pit ore', 'Pit A → Crusher Area'), T03_PRIOR[i]))
    rows.push(priorDay(2, 'Truck 05', label, 'Michael K.', stdSegs('haul', 'Waste haulage', 'Pit B → South dump'), T05_PRIOR[i]))
    rows.push(priorDay(3, 'Truck 08', label, 'John S.', stdSegs('haul', 'Waste haulage', 'Pit B → South dump'), T08_PRIOR[i]))
    rows.push(
      priorDay(
        4,
        'Truck 12',
        label,
        i === 0 ? null : 'Daniel R.',
        i === 0 ? truck12DownDay() : stdSegs('haul', 'Haul cycles — North Pit ore', 'Pit A → Crusher Area'),
        T12_PRIOR[i],
      ),
    )
    rows.push(
      priorDay(
        5,
        'CAT320',
        label,
        'Anna V.',
        i === 3 ? cat320ServiceDay() : stdSegs('task', 'Excavate bench — Pit B', 'Pit B'),
        CAT_PRIOR[i],
      ),
    )
    rows.push(
      priorDay(
        6,
        'Dozer D1',
        label,
        'Joseph A.',
        i === 0 ? dozerOverheatDay() : stdSegs('task', 'Dump face + ramp maintenance', 'South dump'),
        D1_PRIOR[i],
      ),
    )
  })
  return rows
}

const SUMMARY_LABELS: string[] = Array.from({ length: 24 }, (_, i) => `${25 - i} Jun`)

const SUMMARY_VAR: number[] = [2, -3, 4, 0, -2, 3, -4, 1, -1, 5, -5, 2, 0, -3, 3, 1, -2, 4, -1, 0, 2, -4, 1, 3]

const round1 = (value: number): number => Math.round(value * 10) / 10

const summaryMachineDays = (): IOpsMachineDay[] => {
  const rows: IOpsMachineDay[] = []
  SUMMARY_LABELS.forEach((label, i) => {
    const v = SUMMARY_VAR[i]
    const t03Loads = 42 + v
    rows.push(mDay(1, 'Truck 03', label, 'Peter M.', [], 79 + v, 100, t03Loads, Math.round(t03Loads * 37.5), 370 + v * 3, round1(11.2 + v * 0.1), 0, 2100 + v * 18))
    if (label === '14 Jun') {
      rows.push(mDay(4, 'Truck 12', label, 'Daniel R.', [], 18, 42, 9, 340, 90, 2.1, 1, 2890))
    } else {
      const t12Loads = 37 + v
      rows.push(mDay(4, 'Truck 12', label, 'Daniel R.', [], 76 + v, 100, t12Loads, Math.round(t12Loads * 37.5), 340 + v * 3, round1(10.9 + v * 0.1), 0, 2030 + v * 15))
    }
    if (label === '9 Jun') {
      rows.push(mDay(5, 'CAT320', label, null, [], 0, 100, 0, 0, 40, 0.8, 0, 1650))
    } else {
      rows.push(mDay(5, 'CAT320', label, 'Anna V.', [], 87 + v, 100, 0, 0, 310 + v * 2, round1(11.1 + v * 0.08), 0, 1980 + v * 12))
    }
  })
  return rows
}

export const OPS_MACHINE_DAYS: IOpsMachineDay[] = [
  mDay(1, 'Truck 03', 'Yesterday', 'Mark O.', truck03Yesterday(), 85, 100, 79, 2950, 760, 21.6, 0, 4180, 'Day 41 loads · 1,540 t — Night 38 loads · 1,410 t'),
  mDay(4, 'Truck 12', 'Yesterday', 'Daniel R.', truck12Yesterday(), 74, 79, 68, 2550, 520, 18.2, 1, 3640, 'Day 28 loads · 1,050 t — Night 40 loads · 1,500 t'),
  mDay(5, 'CAT320', 'Yesterday', 'Anna V.', cat320Yesterday(), 89, 100, 0, 0, 620, 21.3, 0, 3890, 'Day 10.7 h productive — Night 10.7 h productive'),
  mDay(2, 'Truck 05', 'Yesterday', 'Michael K.', truck05Yesterday(), 75, 100, 36, 1080, 350, 10.6, 0, 2050),
  mDay(3, 'Truck 08', 'Yesterday', 'Peter M.', lateStartSegs('Waste haulage', 'Pit B → South dump'), 80, 100, 40, 1200, 365, 11.2, 0, 2070),
  mDay(6, 'Dozer D1', 'Yesterday', 'Joseph A.', dozerYesterday(), 79, 91, 0, 0, 290, 11.6, 1, 1760),
  ...priorMachineDays(),
  ...summaryMachineDays(),
]

const workerHaulSegs = (taskName: string, location: string, breakMin: number): IOpsTumSegment[] => [
  seg(1, 0, 350, CTX, 'haul', taskName, null, location, null),
  seg(2, 350, 350 + breakMin, NO_TASK_CTX, null, null, null, 'Crib room', `Meal break — ${breakMin} min`),
  seg(3, 350 + breakMin, 700, CTX, 'haul', taskName, null, location, null),
]

const wDay = (
  workerId: number,
  workerName: string,
  dateLabel: string,
  loginAt: string,
  logoutAt: string,
  segments: IOpsTumSegment[],
  breaksMin: number,
  tasksDone: number,
  loads: number,
  issuesReported: number,
  grade: number | null,
  rewardPoints: number,
  activeHours: number,
  unassignedHours: number,
  shiftName?: string,
): IOpsWorkerDay => ({
  workerId,
  workerName,
  role: 'Haul truck operator',
  dateLabel,
  loginAt,
  logoutAt,
  segments,
  breaksMin,
  tasksDone,
  loads,
  issuesReported,
  grade,
  rewardPoints,
  activeHours,
  unassignedHours,
  shiftName,
})

const MARK_PRIOR: (number | null)[][] = [
  [35, 40, 38, 0, 8, 95, 10.6, 0.9],
  [38, 44, 42, 0, 8, 110, 10.8, 0.6],
  [40, 38, 36, 1, 7, 70, 10.3, 1.1],
  [35, 42, 40, 0, 8, 100, 10.7, 0.7],
  [38, 36, 34, 0, 7, 65, 10.2, 1.2],
]

const PETER_PRIOR: (number | null)[][] = [
  [35, 46, 44, 0, 8, 115, 10.9, 0.5],
  [38, 46, 44, 1, null, 90, 10.8, 0.6],
  [40, 43, 41, 0, 6, 55, 10.4, 1.0],
  [35, 45, 43, 0, 7, 85, 10.7, 0.7],
  [38, 44, 42, 0, 7, 80, 10.6, 0.8],
]

const wPrior = (
  workerId: number,
  workerName: string,
  label: string,
  loginAt: string,
  logoutAt: string,
  segments: IOpsTumSegment[],
  t: (number | null)[],
): IOpsWorkerDay =>
  wDay(workerId, workerName, label, loginAt, logoutAt, segments, t[0], t[1], t[2], t[3], t[4], t[5], t[6], t[7])

const priorWorkerDays = (): IOpsWorkerDay[] => {
  const rows: IOpsWorkerDay[] = []
  PRIOR_LABELS.forEach((label, i) => {
    rows.push(
      wPrior(
        10,
        'Mark O.',
        label,
        '05:55',
        '18:02',
        workerHaulSegs('Relief roster — haul cover across fleet', 'Pit A → South dump', MARK_PRIOR[i][0]),
        MARK_PRIOR[i],
      ),
    )
    rows.push(
      wPrior(
        1,
        'Peter M.',
        label,
        '05:57',
        '18:03',
        workerHaulSegs('Haul cycles — Truck 03 · North Pit ore', 'Pit A → Crusher Area', PETER_PRIOR[i][0]),
        PETER_PRIOR[i],
      ),
    )
  })
  rows.push(
    wDay(
      4,
      'Daniel R.',
      '30 Jun',
      '06:02',
      '14:05',
      [
        seg(1, 0, 45, NO_OPERATOR_CTX, null, null, null, 'Go-line', 'Rostered to Truck 12 — machine down overnight, no seat to fill'),
        seg(2, 45, 180, CTX, 'task', 'Assist fitter — Truck 12 tyre teardown', null, 'Workshop Bay 1', null),
        seg(3, 180, 480, NO_TASK_CTX, null, null, null, 'Workshop Bay 1', 'Truck 12 down all day — no spare truck, released early 14:05'),
      ],
      25,
      3,
      0,
      0,
      5,
      15,
      2.3,
      5.8,
    ),
  )
  rows.push(
    wDay(
      4,
      'Daniel R.',
      '29 Jun',
      '05:58',
      '18:01',
      workerHaulSegs('Haul cycles — Truck 12 · North Pit ore', 'Pit A → Crusher Area', 35),
      35,
      41,
      39,
      0,
      7,
      85,
      10.8,
      0.6,
    ),
  )
  return rows
}

const summaryWorkerDays = (): IOpsWorkerDay[] => {
  const rows: IOpsWorkerDay[] = []
  SUMMARY_LABELS.forEach((label, i) => {
    const v = SUMMARY_VAR[i]
    const markGrade = i % 6 === 4 ? null : v >= 4 ? 8 : v <= -4 ? 6 : 7
    const markLoads = 36 + v
    rows.push(
      wDay(10, 'Mark O.', label, '05:55', '18:02', [], 36, markLoads + 2, markLoads, i % 9 === 5 ? 1 : 0, markGrade, markGrade == null ? 30 : markGrade * 12 - 10, round1(10.4 + v * 0.06), round1(0.9 - v * 0.04)),
    )
    const peterGrade = i % 7 === 3 ? null : v >= 3 ? 8 : 7
    const peterLoads = 41 + v
    rows.push(
      wDay(1, 'Peter M.', label, '05:57', '18:03', [], 34, peterLoads + 2, peterLoads, 0, peterGrade, peterGrade == null ? 40 : peterGrade * 12 - 10, round1(10.6 + v * 0.06), round1(0.8 - v * 0.04)),
    )
  })
  return rows
}

const chirwaNightSegs = (): IOpsTumSegment[] => [
  seg(1, 712, 735, NO_TASK_CTX, null, null, null, 'Go-line', 'Night muster + pre-start — logged in 17:52'),
  seg(2, 735, 1065, CTX, 'haul', 'Haul cycles — Truck 03 · night rotation', null, 'Pit A → Crusher Area', null),
  seg(3, 1065, 1110, CTX, 'task', 'Lube + inspection assist — Truck 03', null, 'Fuel bay', null),
  seg(4, 1110, 1130, NO_TASK_CTX, null, null, null, 'Crib room', 'Meal break — 20 min'),
  seg(5, 1130, 1370, CTX, 'haul', 'Haul cycles — Truck 03 · night rotation', null, 'Pit A → Crusher Area', null),
  seg(6, 1370, 1410, CTX, 'task', 'Fuel + washdown — end of night run', null, 'Go-line', null),
]

const mensahNightSegs = (): IOpsTumSegment[] => [
  seg(1, 726, 760, NO_TASK_CTX, null, null, null, 'Workshop Bay 1', 'Waiting on Truck 12 release — tyre change close-out 18:40'),
  seg(2, 760, 1120, CTX, 'haul', 'Haul cycles — Truck 12 · night rotation', null, 'Pit A → Crusher Area', null),
  seg(3, 1120, 1150, NO_TASK_CTX, null, null, null, 'Crib room', 'Meal break — 30 min'),
  seg(4, 1150, 1390, CTX, 'haul', 'Haul cycles — Truck 12 · night rotation', null, 'Pit A → Crusher Area', null),
  seg(5, 1390, 1410, CTX, 'task', 'Fuel + washdown — end of night run', null, 'Go-line', null),
]

export const OPS_WORKER_DAYS: IOpsWorkerDay[] = [
  {
    workerId: 10,
    workerName: 'Mark O.',
    role: 'Haul truck operator',
    dateLabel: 'Yesterday',
    loginAt: '05:52',
    logoutAt: '18:04',
    segments: [
      seg(1, -8, 10, NO_TASK_CTX, null, null, null, 'Muster point', 'Muster + dispatch queue — logged in 05:52'),
      seg(2, 10, 325, CTX, 'haul', 'Haul cycles — Truck 03 · loads 1–21', null, 'Pit A → Crusher Area', null),
      seg(3, 325, 355, CTX, 'task', 'Greasing assist + walkaround — Truck 03', null, 'Fuel bay', null),
      seg(4, 355, 475, CTX, 'haul', 'Haul cycles — Truck 03 · loads 22–29', null, 'Pit A → Crusher Area', 'Load 27 re-tipped at crusher — app count 42 vs weighbridge 41'),
      seg(5, 475, 513, NO_TASK_CTX, null, null, null, 'Crib room', 'Meal break — 38 min'),
      seg(6, 513, 520, NO_TASK_CTX, null, null, null, 'Crusher Area', 'Dispatch queue — crusher crew change'),
      seg(7, 520, 700, CTX, 'haul', 'Haul cycles — Truck 03 · loads 30–42', null, 'Pit A → Crusher Area', null),
      seg(8, 700, 720, NO_TASK_CTX, null, null, null, 'Go-line', 'Washdown queue + handover · windscreen chip logged'),
    ],
    breaksMin: 38,
    tasksDone: 44,
    loads: 42,
    issuesReported: 1,
    grade: 8,
    rewardPoints: 120,
    activeHours: 10.4,
    unassignedHours: 0.7,
  },
  {
    workerId: 1,
    workerName: 'Peter M.',
    role: 'Haul truck operator',
    dateLabel: 'Yesterday',
    loginAt: '06:08',
    logoutAt: '18:02',
    segments: [
      seg(1, 0, 25, NO_TASK_CTX, null, null, null, 'Go-line', 'Reserve call-out — covering a no-show on Truck 08'),
      seg(2, 25, 360, CTX, 'haul', 'Waste haulage — Truck 08', null, 'Pit B → South dump', null),
      seg(3, 360, 395, NO_TASK_CTX, null, null, null, 'Crib room', 'Meal break — 35 min'),
      seg(4, 395, 700, CTX, 'haul', 'Waste haulage — Truck 08', null, 'Pit B → South dump', null),
      seg(5, 700, 720, NO_TASK_CTX, null, null, null, 'Go-line', 'Washdown queue + handover'),
    ],
    breaksMin: 35,
    tasksDone: 41,
    loads: 40,
    issuesReported: 0,
    grade: 7,
    rewardPoints: 80,
    activeHours: 10.7,
    unassignedHours: 0.8,
  },
  {
    workerId: 4,
    workerName: 'Daniel R.',
    role: 'Haul truck operator',
    dateLabel: 'Yesterday',
    loginAt: '05:58',
    logoutAt: '18:00',
    segments: [
      seg(1, -2, 20, NO_TASK_CTX, null, null, null, 'Workshop Bay 1', 'Truck 12 released late from overnight tyre check'),
      seg(2, 20, 440, CTX, 'haul', 'Haul cycles — Truck 12 · mixed ore + waste', null, 'Pit A → Crusher Area', null),
      seg(3, 440, 590, NO_TASK_CTX, null, null, null, 'Main Haul Road', 'Truck 12 down — tyre failure · no spare truck available'),
      seg(4, 590, 625, NO_TASK_CTX, null, null, null, 'Crib room', 'Meal break — 35 min'),
      seg(5, 625, 678, CTX, 'task', 'Assist fitter — torque recheck + release prep', null, 'Workshop Bay 1', null),
      seg(6, 678, 700, CTX, 'haul', 'Roadworthy run — Truck 12 back to pit', null, 'Main Haul Road', null),
      seg(7, 700, 720, NO_TASK_CTX, null, null, null, 'Go-line', 'Handover — tyre failure report filed'),
    ],
    breaksMin: 35,
    tasksDone: 30,
    loads: 28,
    issuesReported: 1,
    grade: 7,
    rewardPoints: 45,
    activeHours: 8.3,
    unassignedHours: 2.5,
  },
  wDay(21, 'S. Chirwa', 'Yesterday', '17:52', '05:42', chirwaNightSegs(), 20, 40, 38, 0, null, 40, 10.6, 0.8, 'Night Shift'),
  wDay(22, 'E. Mensah', 'Yesterday', '18:10', '05:44', mensahNightSegs(), 30, 42, 40, 0, null, 40, 10.3, 1.1, 'Night Shift'),
  ...priorWorkerDays(),
  ...summaryWorkerDays(),
]

export const OPS_TODAY_NOW_MIN = 112

export const OPS_TODAY_NOW_LABEL = '07:52'

export const opsDateLabelOf = (date: Date): string => {
  const now = new Date()
  const startOf = (d: Date): number => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime()
  const diffDays = Math.round((startOf(now) - startOf(date)) / 86400000)
  if (diffDays === 0) return 'Today'
  if (diffDays === 1) return 'Yesterday'
  return format(date, 'd MMM')
}

const liveMinOf = (hhmm: string): number => {
  const parts = hhmm.split(':').map(Number)
  return parts[0] * 60 + parts[1] - 360
}

const liveLabelOf = (min: number): string => {
  const total = (((min + 360) % 1440) + 1440) % 1440
  return `${String(Math.floor(total / 60)).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`
}

const liveActivityOf = (name: string): OpsTumActivity => (name.toLowerCase().includes('haul') ? 'haul' : 'task')

const liveBucketMin = (segments: IOpsTumSegment[], bucket: OpsLossBucket): number =>
  segments.filter((s) => s.bucket === bucket).reduce((total, s) => total + s.toMin - s.fromMin, 0)

export const liveTodayMachineDay = (machineName: string): IOpsMachineDay | null => {
  const task = OPS_LIVE_TASKS.find((t) => t.machine === machineName)
  if (!task) return null
  const down = OPS_MACHINES_DOWN.find((d) => d.name === machineName)
  const downNote = down ? `${down.repairStatus} · est. return ${down.estimatedReturn}` : null
  const segments: IOpsTumSegment[] = []
  if (task.taskStatus === 0) {
    segments.push(seg(1, 0, OPS_TODAY_NOW_MIN, NO_OPERATOR_CTX, null, null, task.id, task.location, task.unassignedReason))
  } else if (task.department === 'maintenance') {
    segments.push(seg(1, 0, OPS_TODAY_NOW_MIN, BREAKDOWN_CTX, 'task', task.name, task.id, task.location, downNote))
  } else if (task.taskStatus === 4 && down) {
    const genMin = Math.max(liveMinOf(task.generatedAt), 0)
    const downMin = liveMinOf(down.since)
    if (genMin > 0) segments.push(seg(1, 0, genMin, NO_TASK_CTX, null, null, null, 'Go-line', 'Muster + dispatch queue'))
    segments.push(seg(2, genMin, downMin, CTX, liveActivityOf(task.name), task.name, task.id, task.location, null))
    segments.push(seg(3, downMin, OPS_TODAY_NOW_MIN, BREAKDOWN_CTX, 'task', task.blockedReason ?? 'Breakdown reported', task.id, task.location, downNote))
  } else {
    const startMin = Math.max(liveMinOf(task.generatedAt) + 6, 0)
    if (startMin > 0) segments.push(seg(1, 0, startMin, NO_TASK_CTX, null, null, null, 'Go-line', `Muster + dispatch queue — task generated ${task.generatedAt}`))
    segments.push(seg(2, startMin, OPS_TODAY_NOW_MIN, CTX, liveActivityOf(task.name), task.name, task.id, task.location, null))
  }
  const productive = liveBucketMin(segments, 'PRODUCTIVE')
  const breakdown = liveBucketMin(segments, 'BREAKDOWN')
  const loads = liveActivityOf(task.name) === 'haul' ? Math.round(productive / 15) : 0
  const known = OPS_MACHINE_DAYS.find((d) => d.machineName === machineName)
  return mDay(
    known?.machineId ?? task.id,
    machineName,
    'Today',
    task.operator,
    segments,
    Math.round((productive / OPS_TODAY_NOW_MIN) * 100),
    100 - Math.round((breakdown / OPS_TODAY_NOW_MIN) * 100),
    loads,
    loads * 38,
    0,
    Math.round(productive / 6) / 10,
    down ? 1 : 0,
    Math.round((OPS_TODAY_NOW_MIN / 60) * 175),
  )
}

export const liveTodayWorkerDay = (workerName: string): IOpsWorkerDay | null => {
  const task = OPS_LIVE_TASKS.find((t) => t.operator === workerName)
  if (!task) return null
  const down = task.machine ? OPS_MACHINES_DOWN.find((d) => d.name === task.machine) : undefined
  const segments: IOpsTumSegment[] = []
  if (task.taskStatus === 4 && down) {
    const genMin = Math.max(liveMinOf(task.generatedAt), 0)
    const downMin = liveMinOf(down.since)
    if (genMin > 0) segments.push(seg(1, 0, genMin, NO_TASK_CTX, null, null, null, 'Go-line', 'Muster + dispatch queue'))
    segments.push(seg(2, genMin, downMin, CTX, liveActivityOf(task.name), task.name, task.id, task.location, null))
    segments.push(seg(3, downMin, OPS_TODAY_NOW_MIN, NO_TASK_CTX, null, null, null, task.location, `${task.machine} down since ${down.since} — waiting on the repair crew`))
  } else {
    const startMin = Math.max(liveMinOf(task.generatedAt) + 6, 0)
    if (startMin > 0) segments.push(seg(1, 0, startMin, NO_TASK_CTX, null, null, null, 'Go-line', 'Muster + dispatch queue'))
    segments.push(seg(2, startMin, OPS_TODAY_NOW_MIN, CTX, liveActivityOf(task.name), task.name, task.id, task.location, null))
  }
  const productive = liveBucketMin(segments, 'PRODUCTIVE')
  const loads = liveActivityOf(task.name) === 'haul' ? Math.round(productive / 15) : 0
  const known = OPS_WORKER_DAYS.find((d) => d.workerName === workerName)
  const roster = OPS_WORKERS.find((w) => w.name === workerName)
  const day = wDay(
    known?.workerId ?? roster?.id ?? task.id,
    workerName,
    'Today',
    liveLabelOf(liveMinOf(task.generatedAt) - 10),
    '—',
    segments,
    0,
    loads,
    loads,
    task.taskStatus === 4 ? 1 : 0,
    null,
    0,
    Math.round(productive / 6) / 10,
    Math.round((OPS_TODAY_NOW_MIN - productive) / 6) / 10,
    'Day Shift',
  )
  return roster ? { ...day, role: roster.role } : day
}

const FUEL_INTENSITY_NOTE = 'Dispensed litres ÷ tonnes moved. Machine-level detail in Fuel → Machines.'

export const OPS_SHIFT_REVIEW: IOpsShiftReviewPack = {
  shiftName: 'Day Shift',
  dateLabel: 'Yesterday',
  scorecard: [
    { label: 'Tonnes hauled', value: '1,840', target: '2,100', avg5: '1,975', unit: 't', tone: 'danger' },
    { label: 'Cost per tonne', value: 'GH₵ 4.10', target: 'GH₵ 3.95', avg5: 'GH₵ 4.01', unit: null, tone: 'danger' },
    { label: 'Fuel intensity', value: '1.42', target: null, avg5: '1.36', unit: 'L/t', tone: 'warning', note: FUEL_INTENSITY_NOTE },
    { label: 'Fleet availability', value: '67', target: '85', avg5: '83', unit: '%', tone: 'danger' },
    { label: 'Utilization', value: '74', target: '80', avg5: '78', unit: '%', tone: 'warning' },
    { label: 'Tasks completed', value: '21 / 26', target: '26', avg5: '23', unit: 'tasks', tone: 'warning' },
    { label: 'Incidents', value: 'L3: 1', target: '0', avg5: '0', unit: null, tone: 'warning' },
    { label: 'Requests', value: '3 raised · 1 overdue', target: null, avg5: null, unit: null, tone: 'warning' },
    { label: 'Handover', value: 'Acknowledged on time', target: null, avg5: null, unit: null, tone: 'success' },
  ],
  waterfall: [
    { label: 'Target', tonnes: 2100, bucket: 'TARGET', drillPath: null },
    { label: 'Truck 12 tyre — 3.1h down', tonnes: -180, bucket: 'BREAKDOWN', drillPath: '/operations/task/9104' },
    { label: 'Crusher starvation — queue ran dry 40min', tonnes: -60, bucket: 'NO_TASK', drillPath: '/operations/plans/1' },
    { label: 'Late crew start — 2 operators 15 min late', tonnes: -12, bucket: 'NO_OPERATOR', drillPath: null },
    { label: 'Unattributed — small variance, kept visible', tonnes: -8, bucket: 'UNATTRIBUTED', drillPath: null },
    { label: 'Actual', tonnes: 1840, bucket: 'ACTUAL', drillPath: null },
  ],
  exceptions: [
    { id: 1, time: '13:20', kind: 'Breakdown', text: 'Truck 12 tyre failure — 3.1h lost, −180 t', severity: 'critical', linkPath: '/operations/task/9104' },
    { id: 2, time: '07:00', kind: 'Request', text: 'REQ-015 overdue — camp roster cover unanswered since 06:20', severity: 'warning', linkPath: '/operations/d/campServices' },
    { id: 3, time: '18:00', kind: 'Program', text: 'Ventilation check program — missed, 0 of 3 checks ran', severity: 'warning', linkPath: '/operations/shift-programs' },
    { id: 4, time: '11:05', kind: 'Incident', text: 'L3 incident open — uncontrolled pressure release at crusher', severity: 'critical', linkPath: '/operations/safety/incidents' },
  ],
  peopleNotes: [
    '2 no-shows covered from reserve roster',
    'MECH-2 capability gap blocked the G2 service 3h',
    'Top loader: Mark O. — 42 loads',
  ],
}

const NIGHT_AVG_NOTE = 'Compared against the last 5 night shifts — never against day shifts.'

const PACK_TODAY_DAY: IOpsShiftReviewPack = {
  shiftName: 'Day Shift',
  dateLabel: 'Today',
  subtitleNote:
    'Provisional — figures update live and lock at 18:00. Compared against the last 5 day shifts.',
  scorecard: [
    { label: 'Tonnes hauled — so far', value: '1,060', target: '2,100', avg5: '1,943', unit: 't', tone: 'success' },
    { label: 'Fleet availability — now', value: '67', target: '85', avg5: '83', unit: '%', tone: 'danger' },
    { label: 'Utilization — so far', value: '71', target: '80', avg5: '77', unit: '%', tone: 'warning' },
    { label: 'Fuel intensity — so far', value: '1.38', target: null, avg5: '1.36', unit: 'L/t', tone: 'warning', note: FUEL_INTENSITY_NOTE },
    { label: 'Tasks completed — so far', value: '9 / 26', target: '26', avg5: '23', unit: 'tasks', tone: 'warning' },
    { label: 'Requests', value: '2 raised · 1 unanswered', target: null, avg5: null, unit: null, tone: 'warning' },
  ],
  waterfall: [],
  exceptions: [
    { id: 1, time: '07:38', kind: 'Breakdown', text: 'Truck 12 blocked — tyre pressure fault on Main Haul Road', severity: 'critical', linkPath: '/operations/task/9104' },
    { id: 2, time: '07:30', kind: 'Coverage', text: 'Coverage gap risk — Main Gate guard unconfirmed for next shift', severity: 'warning', linkPath: '/operations/department' },
  ],
  peopleNotes: [
    'Day crew mustered full — no reserve call-outs so far',
    'MECH-2 capability absent again — Generator G2 service waits for night shift',
  ],
}

const PACK_YESTERDAY_NIGHT: IOpsShiftReviewPack = {
  shiftName: 'Night Shift',
  dateLabel: 'Yesterday',
  subtitleNote: NIGHT_AVG_NOTE,
  scorecard: [
    { label: 'Tonnes hauled', value: '1,910', target: '2,000', avg5: '1,905', unit: 't', tone: 'success' },
    { label: 'Cost per tonne', value: 'GH₵ 3.98', target: 'GH₵ 4.05', avg5: 'GH₵ 4.02', unit: null, tone: 'success' },
    { label: 'Fuel intensity', value: '1.39', target: null, avg5: '1.41', unit: 'L/t', tone: 'success', note: FUEL_INTENSITY_NOTE },
    { label: 'Fleet availability', value: '92', target: '85', avg5: '88', unit: '%', tone: 'success' },
    { label: 'Utilization', value: '78', target: '80', avg5: '77', unit: '%', tone: 'warning' },
    { label: 'Tasks completed', value: '24 / 25', target: '25', avg5: '23', unit: 'tasks', tone: 'success' },
    { label: 'Incidents', value: '0', target: '0', avg5: '0', unit: null, tone: 'success' },
  ],
  waterfall: [
    { label: 'Target', tonnes: 2000, bucket: 'TARGET', drillPath: null },
    { label: 'Crusher recirc surge — night feed held 35 min', tonnes: -55, bucket: 'NO_TASK', drillPath: null },
    { label: 'Truck 03 lube bay — planned night service window', tonnes: -20, bucket: 'PLANNED_MAINT', drillPath: null },
    { label: 'Unattributed — small variance, kept visible', tonnes: -15, bucket: 'UNATTRIBUTED', drillPath: null },
    { label: 'Actual', tonnes: 1910, bucket: 'ACTUAL', drillPath: null },
  ],
  exceptions: [
    { id: 1, time: '23:05', kind: 'Plant', text: 'Crusher recirc surge — feed held 35 min while the loop cleared', severity: 'warning', linkPath: null },
    { id: 2, time: '01:30', kind: 'Maintenance', text: 'Truck 03 lube bay — released 10 min over the planned window', severity: 'warning', linkPath: null },
  ],
  peopleNotes: [
    'Night crew full muster — no reserve call-outs',
    'E. Mensah took Truck 12 from the 18:40 release and held the full rotation',
    'Top loader: S. Chirwa — 38 loads',
  ],
}

const PACK_JUN30_DAY: IOpsShiftReviewPack = {
  shiftName: 'Day Shift',
  dateLabel: '30 Jun',
  scorecard: [
    { label: 'Tonnes hauled', value: '1,710', target: '2,100', avg5: '2,027', unit: 't', tone: 'danger' },
    { label: 'Cost per tonne', value: 'GH₵ 4.22', target: 'GH₵ 3.95', avg5: 'GH₵ 4.00', unit: null, tone: 'danger' },
    { label: 'Fuel intensity', value: '1.47', target: null, avg5: '1.36', unit: 'L/t', tone: 'danger', note: FUEL_INTENSITY_NOTE },
    { label: 'Fleet availability', value: '67', target: '85', avg5: '95', unit: '%', tone: 'danger' },
    { label: 'Utilization', value: '72', target: '80', avg5: '78', unit: '%', tone: 'warning' },
    { label: 'Tasks completed', value: '19 / 25', target: '25', avg5: '23', unit: 'tasks', tone: 'warning' },
    { label: 'Incidents', value: 'L2: 1', target: '0', avg5: '0', unit: null, tone: 'warning' },
  ],
  waterfall: [
    { label: 'Target', tonnes: 2100, bucket: 'TARGET', drillPath: null },
    { label: 'Truck 12 tyre #2 — 5.2h down', tonnes: -290, bucket: 'BREAKDOWN', drillPath: '/operations/analytics/machines/4' },
    { label: 'No operator cover — seat swaps after the tyre failure', tonnes: -70, bucket: 'NO_OPERATOR', drillPath: null },
    { label: 'Unattributed — variance not reconciled to a cause', tonnes: -30, bucket: 'UNATTRIBUTED', drillPath: null },
    { label: 'Actual', tonnes: 1710, bucket: 'ACTUAL', drillPath: null },
  ],
  exceptions: [
    { id: 1, time: '08:55', kind: 'Breakdown', text: 'Truck 12 tyre #2 — sidewall and rim, 5.2h down', severity: 'critical', linkPath: '/operations/analytics/machines/4' },
    { id: 2, time: '10:40', kind: 'Parts', text: 'Tyre 18.00R33 out of stock — warehouse request raised', severity: 'warning', linkPath: null },
    { id: 3, time: '13:05', kind: 'Incident', text: 'L2 incident — pinch-point near miss during the tyre teardown', severity: 'warning', linkPath: '/operations/safety/incidents' },
  ],
  peopleNotes: [
    'Daniel R. released early 14:05 — no spare truck after the tyre failure',
    'Second Truck 12 tyre since 14 Jun — fleet tyre review raised with Maintenance',
    'Top loader: Peter M. — 44 loads',
  ],
}

const PACK_JUN30_NIGHT: IOpsShiftReviewPack = {
  shiftName: 'Night Shift',
  dateLabel: '30 Jun',
  subtitleNote: NIGHT_AVG_NOTE,
  scorecard: [
    { label: 'Tonnes hauled', value: '1,960', target: '2,000', avg5: '1,895', unit: 't', tone: 'success' },
    { label: 'Cost per tonne', value: 'GH₵ 3.91', target: 'GH₵ 4.05', avg5: 'GH₵ 4.03', unit: null, tone: 'success' },
    { label: 'Fuel intensity', value: '1.38', target: null, avg5: '1.41', unit: 'L/t', tone: 'success', note: FUEL_INTENSITY_NOTE },
    { label: 'Utilization', value: '81', target: '80', avg5: '77', unit: '%', tone: 'success' },
    { label: 'Incidents', value: '0', target: '0', avg5: '0', unit: null, tone: 'success' },
  ],
  waterfall: [
    { label: 'Target', tonnes: 2000, bucket: 'TARGET', drillPath: null },
    { label: 'Night crib overlap — crusher feed idle 25 min', tonnes: -25, bucket: 'NO_TASK', drillPath: null },
    { label: 'Unattributed — small variance, kept visible', tonnes: -15, bucket: 'UNATTRIBUTED', drillPath: null },
    { label: 'Actual', tonnes: 1960, bucket: 'ACTUAL', drillPath: null },
  ],
  exceptions: [
    { id: 1, time: '01:10', kind: 'Plant', text: 'Crib overlap — crusher feed idle 25 min', severity: 'warning', linkPath: null },
  ],
  peopleNotes: [
    'Quiet shift — full muster, no call-outs',
    'Top loader: S. Chirwa — 36 loads',
  ],
}

export const OPS_SHIFT_REVIEWS: IOpsShiftReviewEntry[] = [
  { id: 961, dateLabel: 'Today', shiftName: 'Day Shift', tonnes: 1060, target: 2100, locked: false, pack: PACK_TODAY_DAY },
  { id: 960, dateLabel: 'Yesterday', shiftName: 'Day Shift', tonnes: 1840, target: 2100, locked: true, pack: OPS_SHIFT_REVIEW },
  { id: 959, dateLabel: 'Yesterday', shiftName: 'Night Shift', tonnes: 1910, target: 2000, locked: true, pack: PACK_YESTERDAY_NIGHT },
  { id: 958, dateLabel: '30 Jun', shiftName: 'Day Shift', tonnes: 1710, target: 2100, locked: true, pack: PACK_JUN30_DAY },
  { id: 957, dateLabel: '30 Jun', shiftName: 'Night Shift', tonnes: 1960, target: 2000, locked: true, pack: PACK_JUN30_NIGHT },
]

const TREND_LABELS = ['4 Jun', '7 Jun', '10 Jun', '13 Jun', '16 Jun', '19 Jun', '22 Jun', '25 Jun', '28 Jun', '1 Jul']

const trendSeries = (key: string, title: string, unit: string, values: number[], target: number | null): IOpsTrendSeries => ({
  key,
  title,
  unit,
  points: TREND_LABELS.map((label, i) => ({ label, value: values[i], target })),
})

export const OPS_TRENDS: IOpsTrendSeries[] = [
  trendSeries('production', 'Production vs target', 't', [2080, 2110, 1980, 2140, 2060, 1890, 2020, 1970, 2050, 1840], 2100),
  trendSeries('availability', 'Fleet availability', '%', [92, 88, 100, 85, 83, 90, 86, 79, 100, 67], 85),
  trendSeries('utilization', 'Fleet utilization', '%', [81, 80, 82, 79, 78, 80, 77, 76, 78, 74], 80),
]

const LOSS_TREND_BUCKETS: OpsLossBucket[] = [
  'BREAKDOWN',
  'NO_TASK',
  'NO_OPERATOR',
  'PLANNED_MAINT',
  'BLOCKED_CROSS_DEPT',
  'WEATHER_BLAST_HOLD',
]

const lossPoint = (label: string, hours: number[]): IOpsLossTrendPoint => ({
  label,
  hoursByBucket: LOSS_TREND_BUCKETS.map((bucket, i) => ({ bucket, hours: hours[i] })),
})

export const OPS_LOSS_TREND: IOpsLossTrendPoint[] = [
  lossPoint('Wk 17', [9.6, 36, 25, 27, 12, 10.4]),
  lossPoint('Wk 18', [9.9, 35.5, 24.5, 26.8, 11.8, 9.5]),
  lossPoint('Wk 19', [10.4, 35, 24, 26.5, 11.6, 9.5]),
  lossPoint('Wk 20', [10.9, 34.5, 23.5, 26.2, 11.4, 8.5]),
  lossPoint('Wk 21', [11.4, 34, 23, 26, 11.2, 7.4]),
  lossPoint('Wk 22', [12.1, 33.5, 22.5, 25.8, 11, 7.1]),
  lossPoint('Wk 23', [12.6, 33, 22, 25.5, 10.8, 6]),
  lossPoint('Wk 24', [13.3, 32, 21.5, 25.2, 10.6, 5.4]),
  lossPoint('Wk 25', [13.7, 31, 21, 25, 10.4, 3.8]),
  lossPoint('Wk 26', [14.4, 30, 20, 24.8, 10.2, 3.4]),
]

export const OPS_REQUEST_PAIRS: IOpsRequestPairStat[] = [
  { pair: 'Mining → Maintenance', volume: 14, medianAckMin: 22, overduePct: 14 },
  { pair: 'Plant → Maintenance', volume: 6, medianAckMin: 41, overduePct: 33 },
  { pair: 'Camp → HR', volume: 4, medianAckMin: 95, overduePct: 50 },
]

const STARVATION_WEEKS = ['Wk 23', 'Wk 24', 'Wk 25', 'Wk 26']

const starvation = (planId: number, planName: string, counts: number[]): IOpsQueueStarvation => ({
  planId,
  planName,
  weeks: STARVATION_WEEKS.map((label, i) => ({ label, count: counts[i] })),
})

export const OPS_QUEUE_STARVATION: IOpsQueueStarvation[] = [
  starvation(1, 'North Pit Extraction', [1, 2, 3, 5]),
  starvation(2, 'Waste Stripping Phase 2', [0, 1, 0, 1]),
  starvation(3, 'Block B Drill & Blast', [0, 0, 1, 0]),
]

export const OPS_SAFETY_TREND: IOpsSafetyTrendPoint[] = [
  { label: 'Wk 23', l1: 4, l2: 1, l3: 0 },
  { label: 'Wk 24', l1: 3, l2: 2, l3: 0 },
  { label: 'Wk 25', l1: 5, l2: 1, l3: 0 },
  { label: 'Wk 26', l1: 4, l2: 2, l3: 1 },
]

export const OPS_PM_TREND: number[] = [88, 85, 84, 82]

export const OPS_LOSS_PARETO: IOpsLossPareto[] = [
  { bucket: 'BREAKDOWN', hours: 46, costGhs: 84000, topContributor: 'Truck 12 tyres ×3' },
  { bucket: 'NO_TASK', hours: 31, costGhs: 38000, topContributor: 'North Pit queue' },
  { bucket: 'NO_OPERATOR', hours: 22, costGhs: 29000, topContributor: 'MECH-2 gap' },
  { bucket: 'PLANNED_MAINT', hours: 18, costGhs: null, topContributor: 'services' },
  { bucket: 'BLOCKED_CROSS_DEPT', hours: 9, costGhs: 12000, topContributor: 'parts waits' },
  { bucket: 'WEATHER_BLAST_HOLD', hours: 6, costGhs: null, topContributor: '2 blast holds' },
]

export const OPS_DAY_REPORTS: IOpsDayReportRow[] = [
  { id: 954, dateLabel: '2 Jul', shiftName: 'Day Shift', tonnes: 1060, target: 2100, availabilityPct: 67, incidents: 0, locked: false },
  { id: 953, dateLabel: 'Yesterday', shiftName: 'Day Shift', tonnes: 1840, target: 2100, availabilityPct: 67, incidents: 1, locked: true },
  { id: 952, dateLabel: '30 Jun', shiftName: 'Day Shift', tonnes: 1710, target: 2100, availabilityPct: 67, incidents: 1, locked: true },
  { id: 951, dateLabel: '29 Jun', shiftName: 'Day Shift', tonnes: 2130, target: 2100, availabilityPct: 100, incidents: 0, locked: true },
  { id: 950, dateLabel: '28 Jun', shiftName: 'Day Shift', tonnes: 2050, target: 2100, availabilityPct: 100, incidents: 0, locked: true },
  { id: 949, dateLabel: '27 Jun', shiftName: 'Day Shift', tonnes: 1985, target: 2100, availabilityPct: 83, incidents: 1, locked: true },
  { id: 948, dateLabel: '26 Jun', shiftName: 'Day Shift', tonnes: 2000, target: 2100, availabilityPct: 100, incidents: 0, locked: true },
]
